import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown } from 'react-icons/fa';
import { countryPhoneCodes, CountryPhoneCode, getCountryByName, parsePhoneNumber } from './countryPhoneCodes';
import { removeAccents } from '../../../Config';
import styles from './PhoneNumberInput.module.scss';

export type Props = {
    value?: string;
    defaultValue?: string;
    onChange?: (fullPhoneNumber: string) => void;
    onCountryChange?: (country: CountryPhoneCode | null) => void;
    disabled?: boolean;
    placeholder?: string;
    name?: string;
    defaultCountry?: string;
};

const PhoneNumberInput = React.forwardRef<HTMLInputElement, Props>(({
    value,
    onChange,
    onCountryChange,
    disabled = false,
    placeholder,
    name,
    defaultCountry
}, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCountry, setSelectedCountry] = useState<CountryPhoneCode | null>(null);
    const [phoneNumber, setPhoneNumber] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const initializedRef = useRef(false);

    // Initialize from value (from Controller) or defaultValue
    useEffect(() => {
        const currentValue = value || '';

        if (currentValue) {
            // Try to parse the phone number to extract dial code
            const parsed = parsePhoneNumber(currentValue);

            if (parsed) {
                // Number has a recognized dial code (e.g., +351952824148)
                const country = countryPhoneCodes.find(c => c.dialCode === parsed.dialCode);
                if (country) {
                    setSelectedCountry(country);
                    setPhoneNumber(parsed.number);
                    initializedRef.current = true;
                    return;
                }
            }

            // Number doesn't have a dial code (e.g., 952824148)
            // Try to use defaultCountry to detect the country
            if (defaultCountry && !initializedRef.current) {
                const country = getCountryByName(defaultCountry);
                if (country) {
                    // Remove any non-digit characters and set as phone number
                    const cleanNumber = currentValue.replace(/[^\d]/g, '');
                    setSelectedCountry(country);
                    setPhoneNumber(cleanNumber);
                    initializedRef.current = true;
                    return;
                }
            }

            // Can't detect country, just set the number as is
            setPhoneNumber(currentValue.replace(/[^\d]/g, ''));
            initializedRef.current = true;
        } else if (!currentValue && initializedRef.current) {
            // Reset state when value is cleared
            setPhoneNumber('');
            setSelectedCountry(null);
        }
    }, [value, defaultCountry]);

    // Set default country if provided and no phone number is set (new record)
    useEffect(() => {
        if (defaultCountry && !selectedCountry && !phoneNumber && !initializedRef.current) {
            const country = getCountryByName(defaultCountry);
            if (country) {
                setSelectedCountry(country);
            }
        }
    }, [defaultCountry, selectedCountry, phoneNumber]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);

    const handleCountrySelect = useCallback((country: CountryPhoneCode) => {
        setSelectedCountry(country);
        setIsOpen(false);
        setSearchTerm('');

        if (onCountryChange) {
            onCountryChange(country);
        }

        // Trigger onChange with updated full number (always with dial code)
        if (onChange) {
            const fullNumber = phoneNumber ? `${country.dialCode}${phoneNumber}` : '';
            onChange(fullNumber);
        }
    }, [phoneNumber, onChange, onCountryChange]);

    const handlePhoneNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const newNumber = e.target.value.replace(/[^\d]/g, ''); // Only allow digits
        setPhoneNumber(newNumber);

        if (onChange) {
            // Always save with dial code if country is selected
            const fullNumber = selectedCountry && newNumber
                ? `${selectedCountry.dialCode}${newNumber}`
                : newNumber;
            onChange(fullNumber);
        }
    }, [selectedCountry, onChange]);

    const filteredCountries = countryPhoneCodes.filter(country => {
        if (!searchTerm) return true;
        const search = removeAccents(searchTerm).toLowerCase();
        const countryName = removeAccents(t(`common.countries.${country.name}` as any) || country.name).toLowerCase();
        const isoCode = country.isoCode.toLowerCase();
        return countryName.includes(search) || country.dialCode.includes(search) || isoCode.includes(search);
    });

    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div className={styles.container} ref={containerRef}>
            <div className={styles.countrySelector}>
                <button
                    type="button"
                    className={`${styles.countrySelectorButton} ${disabled ? styles.disabled : ''}`}
                    onClick={toggleDropdown}
                    disabled={disabled}
                >
                    {selectedCountry ? (
                        <>
                            <span className={styles.flag}>{selectedCountry.flag}</span>
                            <span className={styles.dialCode}>{selectedCountry.dialCode}</span>
                        </>
                    ) : (
                        <span className={styles.dialCode}>---</span>
                    )}
                    {!disabled && (
                        <FaChevronDown className={`${styles.arrow} ${isOpen ? styles.open : ''}`} />
                    )}
                </button>
            </div>

            <input
                ref={ref}
                type="tel"
                name={name}
                className={styles.phoneInput}
                placeholder={placeholder}
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                disabled={disabled}
            />

            {isOpen && (
                <div className={styles.dropdown}>
                    <div className={styles.searchContainer}>
                        <input
                            ref={searchInputRef}
                            type="text"
                            className={styles.searchInput}
                            placeholder={t('common.search') || 'Search...'}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className={styles.countryList}>
                        {filteredCountries.length > 0 ? (
                            filteredCountries.map(country => (
                                <div
                                    key={country.isoCode}
                                    className={`${styles.countryOption} ${selectedCountry?.isoCode === country.isoCode ? styles.selected : ''}`}
                                    onClick={() => handleCountrySelect(country)}
                                >
                                    <span className={styles.countryFlag}>{country.flag}</span>
                                    <div className={styles.countryInfo}>
                                        <span className={styles.countryName}>
                                            {t(`common.countries.${country.name}` as any) || country.name}
                                        </span>
                                        <span className={styles.countryDialCode}>{country.dialCode}</span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className={styles.noResults}>
                                {t('common.no_results')}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
});

PhoneNumberInput.displayName = 'PhoneNumberInput';

export default PhoneNumberInput;
